import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, UserRole } from '../types';
import { adminLogin } from '../services/authService';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<User>;
  logout: () => void;
  isAdmin: boolean;
  isBrand: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Rehydrate user from localStorage on first load (page refresh support).
  useEffect(() => {
    const saved = localStorage.getItem('adroj_user');
    if (saved) setUser(JSON.parse(saved));
    setIsLoading(false);
  }, []);

  



  const login = async (email: string, password: string): Promise<User> => {
  const result = await adminLogin(email, password);
  const rawUser = result.user;

  // ✅ Compare on the raw lowercase string, BEFORE casting to UserRole —
  //    casting first caused a type error if UserRole's literals are
  //    capitalized ('Admin' | 'Brand') and don't include 'admin'.
  const roleLower = rawUser.role.toLowerCase();

  const displayName =
    roleLower === 'admin'
      ? rawUser.username
      : rawUser.contactPerson || rawUser.companyName;

  const normalizedUser: User = {
    ...rawUser,
    name: displayName as string,
    role: roleLower as UserRole,
  };

  localStorage.setItem('adroj_user', JSON.stringify(normalizedUser));
  setUser(normalizedUser);

  return normalizedUser;
};




  const logout = () => {
    localStorage.removeItem('adroj_token');
    localStorage.removeItem('adroj_user');
    localStorage.removeItem('adroj_access_token');
    localStorage.removeItem('adroj_refresh_token');
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        login,
        logout,
        isAdmin: user?.role?.toLowerCase() === 'admin',
        isBrand: user?.role?.toLowerCase() === 'brand',
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};